class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.abortController = new AbortController();
    this.productCardMedia = new WeakMap();
    this.carouselStates = new Map();
    this.nearViewport = false;
    this.layoutDirty = true;
    const { signal } = this.abortController;

    const tabs = [...this.querySelectorAll(':scope > [role="tab"], :scope > .collection-tabs__nav > .collection-tabs__list > [role="tab"]')];
    if (!tabs.length) return;
    let nav = this.querySelector(':scope > .collection-tabs__nav');
    if (!nav) {
      const list = document.createElement('div');
      list.className = 'collection-tabs__list';
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-label', this.dataset.label || 'Product categories');
      tabs.forEach((tab) => list.append(tab));

      nav = document.createElement('div');
      nav.className = 'collection-tabs__nav';
      const controls = document.createElement('div');
      controls.className = 'collection-tabs__arrows';
      controls.innerHTML = `
        <button type="button" class="collection-tabs__arrow" data-direction="previous" aria-label="Previous product categories">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m10 3-5 5 5 5"/></svg>
        </button>
        <button type="button" class="collection-tabs__arrow" data-direction="next" aria-label="Next product categories">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5"/></svg>
        </button>`;
      nav.append(list, controls);
      this.prepend(nav);
    }
    const list = nav.querySelector('.collection-tabs__list');
    const controls = nav.querySelector('.collection-tabs__arrows');
    this.nav = nav;
    this.list = list;
    this.previous = controls.querySelector('[data-direction="previous"]');
    this.next = controls.querySelector('[data-direction="next"]');
    const onResize = () => {
      if (signal.aborted) return;
      this.syncResponsiveProducts();
      this.layoutDirty = true;
      this.setupProductDots(signal);
      this.scheduleLayout();
    };
    const tabletCarousel = this.closest('.section-resource-list--tablet-carousel');
    this.carouselMedia = matchMedia(tabletCarousel ? '(max-width: 989px)' : '(max-width: 749px)');
    this.syncResponsiveProducts();
    this.carouselMedia.addEventListener('change', onResize, { signal });

    controls.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const direction = button.dataset.direction === 'previous' ? -1 : 1;
      list.scrollBy({ left: direction * Math.max(160, list.clientWidth * 0.65), behavior: 'smooth' });
    }, { signal });
    list.addEventListener('scroll', () => this.scheduleLayout(), { signal, passive: true });
    this.resizeObserver = new ResizeObserver(onResize);
    this.resizeObserver.observe(list);
    document.fonts?.ready.then(() => {
      if (!signal.aborted) onResize();
    });
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (signal.aborted) return;
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.nearViewport = true;
        this.intersectionObserver.disconnect();
        onResize();
      }, { rootMargin: '400px 0px' });
      this.intersectionObserver.observe(this);
    } else {
      this.nearViewport = true;
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab), { signal });
      tab.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    });
    this.addEventListener('change', (event) => this.updateProductCardImage(event), { signal });
    this.addEventListener('click', (event) => {
      const label = event.target instanceof Element
        ? event.target.closest('.variant-option__button-label--has-swatch')
        : null;
      const input = label?.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) {
        requestAnimationFrame(() => {
          if (!signal.aborted) this.selectProductCardImage(input);
        });
      }
    }, { signal, capture: true });
    this.select(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  }

  // Keep a single card instance (including its selected variant) across layouts.
  syncResponsiveProducts() {
    this.querySelectorAll('[data-collection-tab-panel]').forEach((panel) => {
      const grid = panel.querySelector('[data-responsive-product-grid]');
      const carousel = panel.querySelector('[data-responsive-product-carousel]');
      if (!grid || !carousel) return;
      const slides = [...carousel.querySelectorAll('slideshow-slides > .resource-list__slide')];
      if (this.carouselMedia.matches) {
        const items = [...grid.children].filter((item) => item.classList.contains('resource-list__item'));
        if (items.length !== slides.length) return;
        items.forEach((item, index) => {
          // Empty slots have no cached intrinsic height. Lay out their new
          // contents so the carousel reserves its height before scrolling in.
          slides[index].style.contentVisibility = 'visible';
          slides[index].append(item);
        });
      } else {
        slides.forEach((slide) => {
          const item = slide.querySelector(':scope > .resource-list__item');
          if (item) grid.append(item);
        });
      }
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = undefined;
    this.carouselStates?.forEach(({ dots }) => dots.remove());
    this.carouselStates?.clear();
  }

  select(tab, focus = false) {
    if (focus) this.nearViewport = true;
    const targetId = tab.getAttribute('aria-controls');
    this.querySelectorAll('[role="tab"]').forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
      candidate.tabIndex = selected ? 0 : -1;
    });
    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
    if (focus) tab.focus();
    this.layoutDirty = true;
    this.setupProductDots(this.abortController?.signal);
    this.scheduleLayout();
  }

  setupProductDots(signal) {
    if (!this.nearViewport || !this.carouselMedia.matches || signal?.aborted) return;
    const carousels = [...this.querySelectorAll('[role="tabpanel"]:not([hidden]) slideshow-component[ref="resourceListCarouselMobile"]')];
    carousels.forEach((carousel) => {
      if (this.carouselStates.has(carousel)) return;
      const scroller = carousel.querySelector(':scope > slideshow-container > slideshow-slides');
      const slides = [...scroller?.children || []];
      if (!scroller || slides.length < 2) return;

      const dots = document.createElement('div');
      dots.className = 'collection-tabs__product-dots';
      dots.setAttribute('role', 'group');
      dots.setAttribute('aria-label', 'Product slides');
      const state = { scroller, slides, dots, offsets: [], maxScroll: 0, active: -1 };
      slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `collection-tabs__product-dot${index === 0 ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Show product ${index + 1} of ${slides.length}`);
        dot.addEventListener('click', () => {
          scroller.scrollTo({ left: state.offsets[index] ?? 0, behavior: 'smooth' });
        }, { signal });
        dots.append(dot);
      });

      scroller.addEventListener('scroll', () => this.scheduleLayout(), { signal, passive: true });
      carousel.parentElement.after(dots);
      this.carouselStates.set(carousel, state);
      this.resizeObserver.observe(scroller);
      this.layoutDirty = true;
    });
  }

  scheduleLayout() {
    if (!this.nearViewport || this.layoutFrame !== undefined || this.abortController?.signal.aborted) return;
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = undefined;
      // Read geometry for every visible surface before changing any DOM state.
      const width = this.list.clientWidth;
      const scrollWidth = this.list.scrollWidth;
      const scrollLeft = this.list.scrollLeft;
      const updates = [];
      if (this.carouselMedia.matches) this.carouselStates.forEach((state, carousel) => {
        if (carousel.closest('[role="tabpanel"]')?.hidden) return;
        const { scroller, slides } = state;
        if (this.layoutDirty || !state.offsets.length) {
          const clientWidth = scroller.clientWidth;
          if (!clientWidth) return;
          const origin = slides[0].offsetLeft;
          state.offsets = slides.map((slide) => slide.offsetLeft - origin);
          state.maxScroll = scroller.scrollWidth - clientWidth;
        }
        const left = scroller.scrollLeft;
        const active = state.maxScroll > 2 && left >= state.maxScroll - 2
          ? slides.length - 1
          : state.offsets.reduce((closest, offset, index) => {
            const distance = Math.abs(offset - left);
            return distance < closest.distance ? { index, distance } : closest;
          }, { index: 0, distance: Infinity }).index;
        updates.push({ state, active });
      });
      this.layoutDirty = false;
      const overflowing = scrollWidth > width + 1;
      if (this.nav.classList.contains('has-overflow') !== overflowing) this.nav.classList.toggle('has-overflow', overflowing);
      const atStart = scrollLeft <= 20;
      const atEnd = scrollLeft >= scrollWidth - width - 1;
      if (this.previous.disabled !== atStart) this.previous.disabled = atStart;
      if (this.next.disabled !== atEnd) this.next.disabled = atEnd;
      updates.forEach(({ state, active }) => {
        if (state.active === active) return;
        state.active = active;
        [...state.dots.children].forEach((dot, index) => {
          dot.classList.toggle('is-active', index === active);
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      });
    });
  }

  updateProductCardImage(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.name?.includes('-swatch')) return;

    this.selectProductCardImage(input);
  }

  selectProductCardImage(input) {
    if (!(input instanceof HTMLInputElement) || !input.name?.includes('-swatch')) return;

    const mediaId = input.dataset.optionMediaId;
    const card = input.closest('product-card');
    if (!mediaId || !card) return;

    if (typeof card.previewVariant === 'function') {
      card.previewVariant(mediaId, input.dataset.optionImageUrl);
      return;
    }

    const slides = [...card.querySelectorAll('.card-gallery slideshow-slide[slide-id]')];
    const selectedSlide = slides.find((slide) => slide.getAttribute('slide-id') === mediaId);
    const imageUrl = input.dataset.optionImageUrl;
    if (!selectedSlide && !imageUrl) return;

    let media = this.productCardMedia.get(card);
    if (!media) {
      media = new Map(slides.map((slide) => {
        const image = slide.querySelector('img');
        return [slide.getAttribute('slide-id'), image ? {
          src: image.getAttribute('src'),
          srcset: image.getAttribute('srcset'),
          sizes: image.getAttribute('sizes'),
          alt: image.getAttribute('alt'),
          width: image.getAttribute('width'),
          height: image.getAttribute('height'),
        } : null];
      }));
      this.productCardMedia.set(card, media);
    }

    const primarySlide = slides[0];
    const primaryImage = primarySlide?.querySelector('img');
    const selectedMedia = imageUrl
      ? {
          src: imageUrl,
          srcset: null,
          sizes: null,
          alt: primaryImage?.alt ?? '',
          width: '1600',
          height: '1600',
        }
      : media.get(mediaId);
    if (!selectedMedia || !primaryImage) return;

    ['src', 'srcset', 'sizes', 'alt', 'width', 'height'].forEach((attribute) => {
      const value = selectedMedia[attribute];
      if (value == null) primaryImage.removeAttribute(attribute);
      else primaryImage.setAttribute(attribute, value);
    });
    primaryImage.loading = 'eager';
    primarySlide.dataset.selectedMediaId = mediaId;
    slides.forEach((slide, index) => {
      slide.hidden = index !== 0;
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
  }

  onKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const current = tabs.indexOf(event.currentTarget);
    let next = current;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    this.select(tabs[next], true);
  }
}

if (!customElements.get('collection-tabs')) customElements.define('collection-tabs', CollectionTabs);
